import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link as MuiLink
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  School as SchoolIcon,
  Link as LinkIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const UserNotificationDetail = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchNotification();
  }, [notificationId]);

  // Detectar información del dispositivo
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    let platform = 'web';
    let version = 0;

    // Detectar plataforma
    if (/android/i.test(userAgent)) {
      platform = 'android';
      // Extraer versión de Android
      const match = userAgent.match(/Android\s+([\d.]+)/);
      if (match) {
        version = parseInt(match[1]);
      }
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      platform = 'ios';
      // Extraer versión de iOS
      const match = userAgent.match(/OS\s+([\d_]+)/);
      if (match) {
        version = parseInt(match[1].replace('_', '.'));
      }
    } else {
      platform = 'web';
      // Para web, intentar obtener versión del navegador
      const chromeMatch = userAgent.match(/Chrome\/([\d]+)/);
      const firefoxMatch = userAgent.match(/Firefox\/([\d]+)/);
      const safariMatch = userAgent.match(/Version\/([\d]+).*Safari/);

      if (chromeMatch) {
        version = parseInt(chromeMatch[1]);
      } else if (firefoxMatch) {
        version = parseInt(firefoxMatch[1]);
      } else if (safariMatch) {
        version = parseInt(safariMatch[1]);
      }
    }

    return { platform, version };
  };

  // Marcar notificación como leída
  const markAsRead = async (notificationId, userId) => {
    try {
      const deviceInfo = getDeviceInfo();
      const readReceiptId = `${notificationId}_${userId}`;

      const readReceiptRef = doc(db, 'readReceipts', readReceiptId);

      await setDoc(readReceiptRef, {
        deviceInfo: {
          platform: deviceInfo.platform,
          version: deviceInfo.version
        },
        notificationId: notificationId,
        readAt: serverTimestamp(),
        userId: userId
      });

      console.log('Notificación marcada como leída:', readReceiptId);
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  };

  const fetchNotification = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'notifications', notificationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const notificationData = { id: docSnap.id, ...docSnap.data() };

        // Verificar que la notificación sea visible
        if (!notificationData.visible) {
          setError('Esta notificación no está disponible');
          return;
        }

        setNotification(notificationData);

        // Marcar como leída
        const user = auth.currentUser;
        if (user) {
          await markAsRead(notificationId, user.uid);
        }
      } else {
        setError('Notificación no encontrada');
      }
    } catch (error) {
      console.error('Error fetching notification:', error);
      setError('Error al cargar la notificación');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no especificada';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'general': 'General',
      'urgente': 'Urgente',
      'evento': 'Evento',
      'recordatorio': 'Recordatorio',
      'academico': 'Académico',
      'administrativo': 'Administrativo',
      'otros': 'Otros'
    };
    return types[type] || type || 'General';
  };

  const getTypeColor = (type) => {
    const colors = {
      'urgente': '#f44336',
      'evento': COLORS.accent,
      'recordatorio': '#ff9800',
      'academico': '#2196f3',
      'administrativo': '#9c27b0',
      'general': COLORS.secondary,
      'otros': COLORS.text.secondary
    };
    return colors[type] || COLORS.secondary;
  };

  const handleGoBack = () => {
    navigate('/portal');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: COLORS.accent }} size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background, py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={handleGoBack}
              sx={{ mr: 2, color: COLORS.accent }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              Notificación
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background, py: 3 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={handleGoBack}
            sx={{ mr: 2, color: COLORS.accent }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
            {notification?.título}
          </Typography>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: COLORS.surface
          }}
        >
          {/* Título y tipo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: COLORS.text.primary }}>
              {notification?.título}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                icon={<CategoryIcon />}
                label={getTypeLabel(notification?.tipo)}
                sx={{
                  backgroundColor: getTypeColor(notification?.tipo) + '20',
                  color: getTypeColor(notification?.tipo),
                  fontWeight: 'bold'
                }}
              />
            </Box>
          </Box>

          {/* Información detallada */}
          <List sx={{ mb: 3 }}>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <CalendarIcon sx={{ color: COLORS.accent }} />
              </ListItemIcon>
              <ListItemText
                primary="Fecha de publicación"
                secondary={formatDate(notification?.fecha)}
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 'bold',
                  color: COLORS.text.primary
                }}
                secondaryTypographyProps={{
                  variant: 'body1',
                  color: COLORS.text.secondary
                }}
              />
            </ListItem>

            {notification?.cursos && notification.cursos.length > 0 && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <SchoolIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText
                  primary="Dirigido a cursos"
                  secondary={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {notification.cursos.map((curso, index) => (
                        <Chip
                          key={index}
                          label={curso}
                          size="small"
                          sx={{
                            backgroundColor: COLORS.primary + '40',
                            color: COLORS.text.primary
                          }}
                        />
                      ))}
                    </Box>
                  }
                  primaryTypographyProps={{
                    variant: 'body1',
                    fontWeight: 'bold',
                    color: COLORS.text.primary
                  }}
                />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 3 }} />

          {/* Descripción */}
          {notification?.descripción && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLORS.text.primary }}>
                Descripción
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: COLORS.background,
                  borderRadius: 2
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: COLORS.text.secondary,
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {notification.descripción}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Enlaces */}
          {notification?.enlaces && notification.enlaces.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLORS.text.primary }}>
                Enlaces relacionados
              </Typography>
              <List>
                {notification.enlaces.map((enlace, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <LinkIcon sx={{ color: COLORS.accent }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <MuiLink
                          href={enlace.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: COLORS.accent,
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {enlace.nombre}
                        </MuiLink>
                      }
                      secondary={enlace.url}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Adjuntos */}
          {notification?.adjuntos && notification.adjuntos.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLORS.text.primary }}>
                Archivos adjuntos
              </Typography>
              <List>
                {notification.adjuntos.map((adjunto, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <AttachFileIcon sx={{ color: COLORS.accent }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <MuiLink
                          href={adjunto.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: COLORS.accent,
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {adjunto.nombre}
                        </MuiLink>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default UserNotificationDetail;